import React, { useEffect, useState, useCallback, useRef } from 'react';
import { api } from '../utils/api';
import { useToast } from '../components/Toast';
import { useTableControls } from '../hooks/useTableControls';
import SearchBox from '../components/SearchBox';
import StatusTabs from '../components/StatusTabs';
import SortableTh from '../components/SortableTh';
import Pagination from '../components/Pagination';
import Icon from '../components/Icon';

const STATUS_TABS = [
  { value: 'all', label: 'All posts' },
  { value: 'published', label: 'Published' },
  { value: 'draft', label: 'Drafts' },
  { value: 'failed', label: 'Failed' },
];

const POST_TIMEOUT  = 90;  // seconds — text post only (no Reel)
const REEL_TIMEOUT  = 150; // seconds — Reel generation with FFmpeg

// ── Shared step badge ─────────────────────────────────────────────────────────
function StepBadge({ status }) {
  const cfg = {
    pending: { icon: '◦', color: 'var(--muted)',    bg: 'rgba(0,0,0,0.06)',       label: 'pending' },
    active:  { icon: '●', color: 'var(--teal)',     bg: 'rgba(18,114,114,.12)',    label: 'in progress', pulse: true },
    done:    { icon: '✓', color: '#1a7a2e',         bg: 'rgba(26,122,46,.1)',      label: 'done' },
    failed:  { icon: '✕', color: 'var(--danger)',   bg: 'rgba(185,28,28,.1)',      label: 'failed' },
    skipped: { icon: '⚠', color: '#b45309',         bg: 'rgba(180,83,9,.1)',       label: 'skipped' },
  };
  const c = cfg[status] || cfg.pending;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: '0.72rem', fontWeight: 600, padding: '2px 7px',
      borderRadius: 9999, color: c.color, background: c.bg,
      animation: c.pulse ? 'pulse 1.4s ease-in-out infinite' : undefined,
    }}>
      {c.icon} {c.label}
    </span>
  );
}

function StepRow({ label, status }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '5px 10px', borderRadius: 6,
      background: status === 'active' ? 'rgba(18,114,114,.06)' : 'transparent',
      transition: 'background 0.3s',
    }}>
      <span style={{
        fontSize: '0.85rem',
        color: status === 'failed' ? 'var(--danger)' : status === 'skipped' ? '#b45309' : 'var(--navy)',
        fontWeight: status === 'active' ? 600 : 400,
        opacity: status === 'pending' ? 0.55 : 1,
      }}>
        {label}
      </span>
      <StepBadge status={status} />
    </div>
  );
}

// ── Shared audit-log poller hook ──────────────────────────────────────────────
function useAuditPoller(triggerTime, active) {
  const [audit, setAudit] = useState({});
  const [failMsg, setFailMsg] = useState('');

  useEffect(() => {
    if (!active || !triggerTime) return;
    const id = setInterval(async () => {
      try {
        const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Detroit' });
        const r = await api.getAuditLog(`?date=${today}`);
        const recent = (r.items || []).filter(e => e.timestamp >= triggerTime);
        const steps = {};
        recent.forEach(e => {
          steps[e.action_type] = e.success ? 'done' : 'failed';
          if (!e.success && e.error_message) setFailMsg(e.error_message);
        });
        setAudit(steps);
      } catch { /* ignore */ }
    }, 6000);
    return () => clearInterval(id);
  }, [triggerTime, active]);

  return { audit, failMsg };
}

// ── Post generation progress card ─────────────────────────────────────────────
const POST_STEPS = [
  { id: 'brand_voice', label: 'Reading brand voice' },
  { id: 'drafting',    label: 'Writing post with AI' },
  { id: 'safety',      label: 'Safety check' },
  { id: 'publishing',  label: 'Publishing to Facebook' },
];

function postStepStatuses(elapsed, audit) {
  const done   = t => audit[t] === 'done';
  const failed = t => audit[t] === 'failed';
  return {
    brand_voice: elapsed > 4 ? 'done' : elapsed > 0 ? 'active' : 'pending',
    drafting:    done('POST_DRAFTED')  ? 'done'
               : failed('POST_FAILED')  ? 'failed'
               : elapsed > 4            ? 'active' : 'pending',
    safety:      failed('SAFETY_BLOCK') ? 'failed'
               : done('POST_PUBLISHED') ? 'done'
               : done('POST_DRAFTED')   ? 'active' : 'pending',
    publishing:  done('POST_PUBLISHED')                             ? 'done'
               : failed('POST_FAILED') && !failed('SAFETY_BLOCK')  ? 'failed'
               : done('POST_DRAFTED')                               ? 'active' : 'pending',
  };
}

function PostGenerationProgress({ knownIds, triggerTime, onNewPost, onTimeout }) {
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult]   = useState(null);
  const [newPost, setNewPost] = useState(null);
  const { audit, failMsg }    = useAuditPoller(triggerTime, !result);

  useEffect(() => {
    const start = Date.now();
    let done = false;
    const ticker = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 500);

    const postPoller = setInterval(async () => {
      if (done) return;
      try {
        const r = await api.getPosts();
        const found = (r.items || []).find(p => !knownIds.has(p.post_id));
        if (found) { finish(); setNewPost(found); setResult('success'); setTimeout(() => onNewPost(found), 1400); }
      } catch { /* ignore */ }
    }, 8000);

    const deadline = setTimeout(() => { finish(); setResult('timeout'); onTimeout(); }, POST_TIMEOUT * 1000);

    function finish() {
      if (done) return; done = true;
      clearInterval(ticker); clearInterval(postPoller); clearTimeout(deadline);
    }
    return finish;
  }, [knownIds, triggerTime, onNewPost, onTimeout]);

  // Detect hard failure from audit log
  useEffect(() => {
    if (!result && (audit['POST_FAILED'] === 'failed' || audit['SAFETY_BLOCK'] === 'failed')) {
      setResult('failed');
    }
  }, [audit, result]);

  const statuses  = postStepStatuses(elapsed, audit);
  const doneCount = POST_STEPS.filter(s => statuses[s.id] === 'done').length;
  const hasFail   = POST_STEPS.some(s => statuses[s.id] === 'failed');
  const pct       = result === 'success' ? 100
                  : hasFail              ? Math.min(doneCount / POST_STEPS.length * 100, 80)
                  : Math.min(Math.max(doneCount / POST_STEPS.length * 100, elapsed / POST_TIMEOUT * 85), 92);

  if (result === 'success' && newPost) {
    return (
      <div className="card" style={{ marginBottom: 24, borderLeft: '4px solid #1a7a2e' }}>
        <div className="card-body">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <span style={{ fontSize: '1.5rem' }}>✅</span>
            <div>
              <p style={{ fontWeight: 700, color: '#1a7a2e', marginBottom: 2 }}>Post published to Facebook!</p>
              <p style={{ fontSize: '0.82rem', color: 'var(--muted)', margin: 0 }}>
                <span className={`badge badge-${newPost.status}`}>{newPost.status}</span>
                {newPost.facebook_post_id && <span style={{ marginLeft: 8 }}>· FB ID: {newPost.facebook_post_id}</span>}
              </p>
            </div>
          </div>
          <div style={{ background: 'var(--cream)', borderRadius: 8, padding: '12px 16px', fontSize: '0.9rem', lineHeight: 1.65 }}>
            {newPost.content}
          </div>
        </div>
      </div>
    );
  }

  if (result === 'failed') {
    return (
      <div className="card" style={{ marginBottom: 24, borderLeft: '4px solid var(--danger)' }}>
        <div className="card-body" style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <span style={{ fontSize: '1.4rem' }}>✕</span>
          <div>
            <p style={{ fontWeight: 700, color: 'var(--danger)', marginBottom: 4 }}>Post generation failed</p>
            {failMsg && <p style={{ fontSize: '0.83rem', color: 'var(--muted)', margin: '0 0 6px' }}>{failMsg}</p>}
            <p style={{ fontSize: '0.82rem', color: 'var(--muted)', margin: 0 }}>Check the <a href="/marketing/audit" style={{ color: 'var(--teal)' }}>Audit Log</a> for details.</p>
          </div>
        </div>
      </div>
    );
  }

  if (result === 'timeout') {
    return (
      <div className="card" style={{ marginBottom: 24, borderLeft: '4px solid var(--gold)' }}>
        <div className="card-body" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <span style={{ fontSize: '1.4rem' }}>⚠️</span>
          <div>
            <p style={{ fontWeight: 600, marginBottom: 2 }}>Taking longer than expected</p>
            <p style={{ fontSize: '0.83rem', color: 'var(--muted)', margin: 0 }}>
              Check the <a href="/marketing/audit" style={{ color: 'var(--teal)' }}>Audit Log</a> or refresh in a minute.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card" style={{ marginBottom: 24, borderLeft: '4px solid var(--teal)' }}>
      <div className="card-body">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <div style={{ fontSize: '1.6rem', flexShrink: 0, marginTop: 2 }}>📝</div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
              <p style={{ fontWeight: 600, margin: 0 }}>Generating your Facebook post…</p>
              <span style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>~{Math.max(0, POST_TIMEOUT - elapsed)}s</span>
            </div>
            <div style={{ background: 'rgba(0,0,0,0.08)', borderRadius: 9999, height: 6, overflow: 'hidden', marginBottom: 14 }}>
              <div style={{ width: `${pct}%`, height: '100%', background: hasFail ? 'var(--danger)' : 'linear-gradient(90deg, var(--teal), #4aa8a8)', borderRadius: 9999, transition: 'width 0.8s ease-out' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {POST_STEPS.map(s => <StepRow key={s.id} label={s.label} status={statuses[s.id]} />)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Reel generation progress card ─────────────────────────────────────────────
const REEL_STEPS = [
  { id: 'reel_flyer', label: 'Generating flyer image' },
  { id: 'reel_gen',   label: 'Creating video (FFmpeg)' },
  { id: 'reel_pub',   label: 'Publishing to Facebook Reels' },
];

function reelStepStatuses(elapsed, audit) {
  const done   = t => audit[t] === 'done';
  const failed = t => audit[t] === 'failed';
  return {
    reel_flyer: done('REEL_FLYER_GENERATED') ? 'done'
               : failed('REEL_FAILED')        ? 'failed'
               : elapsed > 3                  ? 'active' : 'pending',
    reel_gen:   done('REEL_GENERATED')        ? 'done'
               : failed('REEL_FAILED')        ? 'failed'
               : done('REEL_FLYER_GENERATED') ? 'active' : 'pending',
    reel_pub:   done('REEL_PUBLISHED')        ? 'done'
               : failed('REEL_FAILED')        ? 'failed'
               : done('REEL_GENERATED')       ? 'active' : 'pending',
  };
}

function ReelGenerationProgress({ triggerTime, onDone }) {
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult]   = useState(null);
  const { audit, failMsg }    = useAuditPoller(triggerTime, !result);

  useEffect(() => {
    const start = Date.now();
    let done = false;
    const ticker   = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 500);
    const deadline = setTimeout(() => { finish(); setResult('timeout'); }, REEL_TIMEOUT * 1000);
    function finish() {
      if (done) return; done = true;
      clearInterval(ticker); clearTimeout(deadline);
    }
    return finish;
  }, []);

  // Detect completion from audit log
  useEffect(() => {
    if (result) return;
    if (audit['REEL_PUBLISHED'] === 'done') {
      setResult('success');
      setTimeout(() => onDone(), 2000);
    } else if (audit['REEL_FAILED'] === 'failed') {
      setResult('failed');
    }
  }, [audit, result, onDone]);

  const statuses  = reelStepStatuses(elapsed, audit);
  const doneCount = REEL_STEPS.filter(s => statuses[s.id] === 'done').length;
  const hasFail   = REEL_STEPS.some(s => statuses[s.id] === 'failed');
  const pct       = result === 'success' ? 100
                  : hasFail              ? Math.min(doneCount / REEL_STEPS.length * 100, 80)
                  : Math.min(Math.max(doneCount / REEL_STEPS.length * 100, elapsed / REEL_TIMEOUT * 85), 92);

  const borderColor = result === 'success' ? '#1a7a2e'
                    : result === 'failed'  ? 'var(--danger)'
                    : result === 'timeout' ? 'var(--gold)'
                    : '#9333ea';

  if (result === 'success') {
    return (
      <div className="card" style={{ marginBottom: 24, borderLeft: `4px solid ${borderColor}` }}>
        <div className="card-body" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <span style={{ fontSize: '1.5rem' }}>🎬</span>
          <div>
            <p style={{ fontWeight: 700, color: '#1a7a2e', marginBottom: 2 }}>Reel published to Facebook!</p>
            <p style={{ fontSize: '0.83rem', color: 'var(--muted)', margin: 0 }}>Check your Page's Reels tab — it may take a few minutes to process.</p>
          </div>
        </div>
      </div>
    );
  }

  if (result === 'failed') {
    return (
      <div className="card" style={{ marginBottom: 24, borderLeft: `4px solid ${borderColor}` }}>
        <div className="card-body" style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <span style={{ fontSize: '1.4rem' }}>✕</span>
          <div>
            <p style={{ fontWeight: 700, color: 'var(--danger)', marginBottom: 4 }}>Reel generation failed</p>
            {failMsg && <p style={{ fontSize: '0.83rem', color: 'var(--muted)', margin: '0 0 6px' }}>{failMsg}</p>}
            <p style={{ fontSize: '0.82rem', color: 'var(--muted)', margin: 0 }}>Check the <a href="/marketing/audit" style={{ color: 'var(--teal)' }}>Audit Log</a> for details.</p>
          </div>
        </div>
      </div>
    );
  }

  if (result === 'timeout') {
    return (
      <div className="card" style={{ marginBottom: 24, borderLeft: `4px solid ${borderColor}` }}>
        <div className="card-body" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <span style={{ fontSize: '1.4rem' }}>⚠️</span>
          <div>
            <p style={{ fontWeight: 600, marginBottom: 2 }}>Reel is still processing</p>
            <p style={{ fontSize: '0.83rem', color: 'var(--muted)', margin: 0 }}>
              FFmpeg can take up to 2 minutes. Check the <a href="/marketing/audit" style={{ color: 'var(--teal)' }}>Audit Log</a> for status.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card" style={{ marginBottom: 24, borderLeft: `4px solid ${borderColor}` }}>
      <div className="card-body">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <div style={{ fontSize: '1.6rem', flexShrink: 0, marginTop: 2 }}>🎬</div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
              <p style={{ fontWeight: 600, margin: 0 }}>Generating Facebook Reel…</p>
              <span style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>~{Math.max(0, REEL_TIMEOUT - elapsed)}s</span>
            </div>
            <div style={{ background: 'rgba(0,0,0,0.08)', borderRadius: 9999, height: 6, overflow: 'hidden', marginBottom: 14 }}>
              <div style={{ width: `${pct}%`, height: '100%', background: hasFail ? 'var(--danger)' : 'linear-gradient(90deg, #9333ea, #c084fc)', borderRadius: 9999, transition: 'width 0.8s ease-out' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {REEL_STEPS.map(s => <StepRow key={s.id} label={s.label} status={statuses[s.id]} />)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function PostsPage() {
  const toast = useToast();
  const [posts, setPosts]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [generating, setGenerating]     = useState(false);
  const [generatingReel, setGeneratingReel] = useState(false);
  const [expanded, setExpanded]         = useState(null);
  const [lastUpdated, setLastUpdated]   = useState(null);

  const knownIdsRef    = useRef(new Set());
  const triggerTimeRef = useRef('');
  const reelTriggerRef = useRef('');

  const load = useCallback(async () => {
    try {
      const r = await api.getPosts();
      const items = r.items || [];
      setPosts(items);
      setLastUpdated(new Date());
      return items;
    } catch {
      toast('Failed to load posts', 'error');
      return [];
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async () => {
    const current = await load();
    knownIdsRef.current  = new Set(current.map(p => p.post_id));
    triggerTimeRef.current = new Date(Date.now() - 5000).toISOString();
    try {
      await api.triggerPost();
      setGenerating(true);
    } catch (e) {
      toast(e.message || 'Failed to trigger post generation', 'error');
    }
  };

  const handleGenerateReel = async () => {
    reelTriggerRef.current = new Date(Date.now() - 5000).toISOString();
    try {
      await api.triggerReel();
      setGeneratingReel(true);
    } catch (e) {
      toast(e.message || 'Failed to trigger Reel generation', 'error');
    }
  };

  const handleNewPost = useCallback(async (post) => {
    setGenerating(false);
    await load();
    setExpanded(post.post_id);
  }, [load]);

  const handlePostTimeout = useCallback(() => { setGenerating(false); load(); }, [load]);
  const handleReelDone    = useCallback(() => { setGeneratingReel(false); }, []);

  const tc = useTableControls({
    items: posts,
    searchFields: (p) => [p.content, p.source, p.facebook_post_id],
    statusOf: (p) => p.status,
    sorters: {
      date: (p) => p.date || '',
      status: (p) => p.status || '',
      source: (p) => p.source || '',
    },
    defaultSort: 'date',
    defaultDir: 'desc',
    pageSize: 10,
  });

  return (
    <>
      <div className="page-header">
        <h1>Posts</h1>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            className="btn btn-teal"
            onClick={handleGenerate}
            disabled={generating || generatingReel}
          >
            {generating ? '⏳ Generating…' : '📝 Generate Post'}
          </button>
          <button
            className="btn"
            onClick={handleGenerateReel}
            disabled={generating || generatingReel || posts.length === 0}
            title={posts.length === 0 ? 'Generate a text post first' : 'Create a Reel from the most recent post'}
            style={{
              background: generatingReel ? 'rgba(147,51,234,.15)' : 'rgba(147,51,234,.1)',
              color: '#7c3aed', border: '1px solid rgba(147,51,234,.3)',
              opacity: (generating || generatingReel || posts.length === 0) ? 0.55 : 1,
            }}
          >
            {generatingReel ? '⏳ Creating Reel…' : '🎬 Generate Reel'}
          </button>
        </div>
      </div>

      {generating && (
        <PostGenerationProgress
          knownIds={knownIdsRef.current}
          triggerTime={triggerTimeRef.current}
          onNewPost={handleNewPost}
          onTimeout={handlePostTimeout}
        />
      )}

      {generatingReel && (
        <ReelGenerationProgress
          triggerTime={reelTriggerRef.current}
          onDone={handleReelDone}
        />
      )}

      {!loading && posts.length > 0 && (
        <section className="summary-bar">
          <div className="summary-item">
            <span className="summary-value">{posts.length}</span>
            <span className="summary-label">Total posts</span>
          </div>
          <div className="summary-divider" />
          <div className="summary-item success">
            <span className="summary-value">{tc.counts.published || 0}</span>
            <span className="summary-label">Published</span>
          </div>
          <div className="summary-divider" />
          <div className="summary-item attention">
            <span className="summary-value">{tc.counts.draft || 0}</span>
            <span className="summary-label">Drafts</span>
          </div>
          <div className="summary-divider" />
          <div className="summary-item danger">
            <span className="summary-value">{tc.counts.failed || 0}</span>
            <span className="summary-label">Failed</span>
          </div>
          {lastUpdated && (
            <div className="summary-updated">
              <span className="live-dot" />
              Updated {lastUpdated.toLocaleTimeString()}
            </div>
          )}
        </section>
      )}

      <section className="list-panel">
        <div className="panel-toolbar">
          <div className="panel-toolbar-title">
            <h2>All posts</h2>
            <span>{tc.sorted.length} records</span>
          </div>
          <div className="panel-toolbar-actions">
            <SearchBox value={tc.search} onChange={tc.setSearch} placeholder="Search posts…" />
            <button type="button" className="icon-btn" onClick={load} title="Refresh">
              <Icon name="refresh" />
            </button>
          </div>
        </div>

        <StatusTabs
          tabs={STATUS_TABS}
          active={tc.activeStatus}
          onChange={tc.setActiveStatus}
          counts={tc.counts}
          total={posts.length}
        />

        <div className="table-wrap">
          {loading ? (
            <div className="empty-state"><p>Loading…</p></div>
          ) : posts.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">📝</div>
              <p>No posts yet. Click "Generate Post" to create the first one.</p>
            </div>
          ) : tc.paginated.length === 0 ? (
            <div className="empty-state"><p>No posts match your search or filters.</p></div>
          ) : (
            <table>
              <thead>
                <tr>
                  <SortableTh col="date" label="Date" sortCol={tc.sortCol} sortDir={tc.sortDir} onSort={tc.handleSort} />
                  <th>Content</th>
                  <SortableTh col="status" label="Status" sortCol={tc.sortCol} sortDir={tc.sortDir} onSort={tc.handleSort} />
                  <SortableTh col="source" label="Source" sortCol={tc.sortCol} sortDir={tc.sortDir} onSort={tc.handleSort} />
                  <th>FB Post ID</th>
                </tr>
              </thead>
              <tbody>
                {tc.paginated.map(p => (
                  <React.Fragment key={p.post_id}>
                    <tr
                      style={{ cursor: 'pointer', background: p.post_id === expanded ? 'var(--cream)' : undefined, transition: 'background 0.2s' }}
                      onClick={() => setExpanded(expanded === p.post_id ? null : p.post_id)}
                    >
                      <td className="td-mono">{p.date}</td>
                      <td className="td-truncate">{p.content}</td>
                      <td><span className={`badge badge-${p.status} badge-dot`}>{p.status}</span></td>
                      <td style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>{p.source}</td>
                      <td className="td-mono" style={{ fontSize: '0.75rem' }}>
                        {p.facebook_post_id
                          ? <span style={{ color: 'var(--teal)' }}>{p.facebook_post_id}</span>
                          : <span style={{ color: 'var(--muted)' }}>—</span>}
                      </td>
                    </tr>
                    {expanded === p.post_id && (
                      <tr>
                        <td colSpan={5} style={{ background: 'var(--cream)', padding: '16px 20px' }}>
                          <p style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem', lineHeight: 1.7, marginBottom: p.hashtags?.length ? 12 : 0 }}>
                            {p.content}
                          </p>
                          {p.hashtags?.length > 0 && (
                            <div className="tag-list">
                              {p.hashtags.map(h => <span key={h} className="tag teal">{h}</span>)}
                            </div>
                          )}
                          {p.error && (
                            <p style={{ marginTop: 10, fontSize: '0.82rem', color: 'var(--danger)' }}>Error: {p.error}</p>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <Pagination
          pageSize={tc.pageSize}
          setPageSize={tc.setPageSize}
          currentPage={tc.currentPage}
          setCurrentPage={tc.setCurrentPage}
          totalPages={tc.totalPages}
          pageStart={tc.pageStart}
          pageEnd={tc.pageEnd}
          total={tc.sorted.length}
          paginationItems={tc.paginationItems}
        />
      </section>
    </>
  );
}
